import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../../api";
import { TOUR_DEFAULTS, TOUR_ICON_LABELS } from "../../components/tourSteps";
import { Trash2, Plus, RotateCcw } from "lucide-react";

const ROLES = [["courier", "Carriers"], ["company", "Shippers"], ["admin", "Control Tower"]];

export default function TourTab() {
  const [steps, setSteps] = useState(null);
  const [role, setRole] = useState("courier");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/tour-steps")
      .then((r) => setSteps(r.data.steps || JSON.parse(JSON.stringify(TOUR_DEFAULTS))))
      .catch((e) => { toast.error(fmtErr(e)); setSteps(JSON.parse(JSON.stringify(TOUR_DEFAULTS))); });
  }, []);

  if (!steps) return <p className="text-sm text-[#A09D95]">Loading…</p>;
  const list = steps[role] || [];

  const update = (i, k, v) => {
    const next = { ...steps, [role]: list.map((s, j) => (j === i ? { ...s, [k]: v || null } : s)) };
    setSteps(next);
  };
  const addStep = () => setSteps({ ...steps, [role]: [...list, { icon: "sparkles", t: "New step", d: "Explain what to do here.", path: null, target: null }] });
  const removeStep = (i) => setSteps({ ...steps, [role]: list.filter((_, j) => j !== i) });

  const save = async (payload) => {
    setBusy(true);
    try {
      await api.put("/admin/tour-steps", { steps: payload });
      setSteps(payload);
      toast.success("Tour updated — live for every user immediately");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="tour-tab">
      <div className="cr-card !p-5">
        <p className="text-sm font-bold mb-1">Platform tour steps</p>
        <p className="text-xs text-[#78756E] leading-relaxed">The guided tour new accounts see after registering. Edit text, pick an icon, and optionally point a step at an element on a page (path + its data-testid) to show the arrow and highlight. Changes go live for everyone the moment you save.</p>
      </div>
      <div className="flex gap-1 bg-[#EFECE2] p-1 rounded-full w-fit" data-testid="tour-role-picker">
        {ROLES.map(([r, label]) => (
          <button key={r} data-testid={`tour-role-${r}`} onClick={() => setRole(r)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${role === r ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}>{label}</button>
        ))}
      </div>
      <div className="space-y-3">
        {list.map((s, i) => (
          <div key={i} className="cr-card !p-4 space-y-2.5" data-testid={`tour-step-${i}`}>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#1C1B1A] text-[#F2D06B] text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <input data-testid={`tour-step-${i}-title`} value={s.t} onChange={(e) => update(i, "t", e.target.value)}
                className="cr-input !py-1.5 !text-xs font-semibold flex-1" placeholder="Step title" />
              <select data-testid={`tour-step-${i}-icon`} value={s.icon} onChange={(e) => update(i, "icon", e.target.value)}
                className="cr-input !py-1.5 !text-xs !w-36">
                {Object.entries(TOUR_ICON_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <button data-testid={`tour-step-${i}-delete`} onClick={() => removeStep(i)}
                className="w-8 h-8 rounded-full hover:bg-red-50 text-[#A09D95] hover:text-red-500 flex items-center justify-center transition-colors shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <textarea data-testid={`tour-step-${i}-body`} value={s.d} onChange={(e) => update(i, "d", e.target.value)}
              rows={2} className="cr-input !text-xs w-full" placeholder="What this step explains" />
            <div className="flex gap-2">
              <input data-testid={`tour-step-${i}-path`} value={s.path || ""} onChange={(e) => update(i, "path", e.target.value)}
                className="cr-input !py-1.5 !text-xs !w-36" placeholder="Path (e.g. /courier)" />
              <input data-testid={`tour-step-${i}-target`} value={s.target || ""} onChange={(e) => update(i, "target", e.target.value)}
                className="cr-input !py-1.5 !text-xs flex-1" placeholder="Point at element (data-testid, optional)" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="tour-add-step" onClick={addStep} className="cr-btn-outline !text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add step</button>
        <button data-testid="tour-save" onClick={() => save(steps)} disabled={busy} className="cr-btn-dark !text-xs disabled:opacity-40">{busy ? "Saving…" : "Save & publish"}</button>
        <button data-testid="tour-reset" onClick={() => save(JSON.parse(JSON.stringify(TOUR_DEFAULTS)))} disabled={busy}
          className="cr-btn-outline !text-xs flex items-center gap-1.5 text-[#A09D95]"><RotateCcw className="w-3.5 h-3.5" /> Reset all to defaults</button>
      </div>
    </div>
  );
}
